import { z } from "zod";
import { mcpServerIconPathSchema } from "@/lib/core/mcp-credentials/mcp-credential-management.contract.ts";

export const mcpServerIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z0-9][a-z0-9._-]*$/u);

export const mcpConnectionAuthorizationIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/u);

const currentMcpConnectionRequestSchema = z.discriminatedUnion("action", [
    z
        .object({
            action: z.literal("state"),
            serverId: mcpServerIdSchema,
        })
        .strict(),
    z
        .object({
            action: z.literal("authorize"),
            serverId: mcpServerIdSchema,
            connectionId: z.uuid().optional(),
            linkId: z.uuid().optional(),
        })
        .strict(),
    z
        .object({
            action: z.literal("status"),
            serverId: mcpServerIdSchema,
            authorizationId: mcpConnectionAuthorizationIdSchema,
        })
        .strict(),
]);

const legacyMcpConnectionRequestSchema = z
    .discriminatedUnion("action", [
        z
            .object({
                action: z.literal("state"),
                provider: mcpServerIdSchema,
            })
            .strict(),
        z
            .object({
                action: z.literal("authorize"),
                provider: mcpServerIdSchema,
                connectionId: z.uuid().optional(),
                linkId: z.uuid().optional(),
            })
            .strict(),
        z
            .object({
                action: z.literal("status"),
                provider: mcpServerIdSchema,
                authorizationId: mcpConnectionAuthorizationIdSchema,
            })
            .strict(),
    ])
    .transform(({ provider, ...rest }) => ({ ...rest, serverId: provider }));

export const mcpConnectionRequestSchema = z.union([
    currentMcpConnectionRequestSchema,
    legacyMcpConnectionRequestSchema,
]);

export const mcpConnectionAccountSchema = z.object({
    connectionId: z.uuid(),
    accountLabel: z.string().nullable(),
    status: z.enum(["connected", "needs_reconnect"]),
});

export const mcpConnectionServerSchema = z.object({
    name: z.string().min(1).max(160),
    iconPath: mcpServerIconPathSchema.nullable().catch(null),
});

export const mcpConnectionStateResponseSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("available"),
        server: mcpConnectionServerSchema,
        accounts: z.array(mcpConnectionAccountSchema).max(50),
    }),
    z.object({ state: z.literal("unavailable") }),
]);

export const mcpConnectionAuthorizeResponseSchema = z.object({
    connectionId: z.uuid(),
    authorizationId: mcpConnectionAuthorizationIdSchema,
    authorizationUrl: z.url({ protocol: /^https?$/u }),
    expiresAt: z.iso.datetime({ offset: true }),
});

export const mcpConnectionStatusResponseSchema = z.discriminatedUnion(
    "status",
    [
        z.object({ status: z.literal("pending") }),
        z.object({
            status: z.literal("connected"),
            connectionId: z.uuid().optional(),
            accountLabel: z.string().nullable(),
            grantedScopes: z.array(z.string()),
        }),
        z.object({
            status: z.literal("failed"),
            reason: z.enum(["declined", "error"]),
        }),
        z.object({ status: z.literal("expired") }),
    ],
);

export const mcpConnectionErrorResponseSchema = z.object({
    error: z.enum([
        "unauthorized",
        "invalid_request",
        "connection_service_unavailable",
        "connection_service_failed",
    ]),
});

export type McpConnectionRequest = z.infer<typeof mcpConnectionRequestSchema>;
export type McpConnectionAccount = z.infer<typeof mcpConnectionAccountSchema>;
export type McpConnectionStateResponse = z.infer<
    typeof mcpConnectionStateResponseSchema
>;
export type McpConnectionAuthorizeResponse = z.infer<
    typeof mcpConnectionAuthorizeResponseSchema
>;
export type McpConnectionStatusResponse = z.infer<
    typeof mcpConnectionStatusResponseSchema
>;
export type McpConnectionErrorResponse = z.infer<
    typeof mcpConnectionErrorResponseSchema
>;
export type McpConnectionServer = z.infer<typeof mcpConnectionServerSchema>;
